import React, { useState, useMemo } from 'react';
import { Card, Table, Modal, Button, Form, Spinner, Tabs, Tab, InputGroup } from 'react-bootstrap';
import { formatDate } from '../../utils/dateFormatter';
import { useConfigBasedTimeFilter, getStatusText, getToggleButtonText } from '../../utils/timeFilter';
import { useTags } from '../../services/api/hooks/useTags';
import { validateTagsCsvFile } from '../../utils/csvUtils';
import { Tag } from '../../services/api/types';

const Tags = () => {
  const [activeTab, setActiveTab] = useState('manual');
  const [newTagName, setNewTagName] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentTag, setCurrentTag] = useState<Tag | null>(null);
  const [originalTagName, setOriginalTagName] = useState<string>('');
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [tagToDeleteId, setTagToDeleteId] = useState<number | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const { showAll: showAllSystemTags, setShowAll: setShowAllSystemTags, timeFilter: systemTimeFilter } = useConfigBasedTimeFilter();
  const { showAll: showAllManualTags, setShowAll: setShowAllManualTags, timeFilter: manualTimeFilter } = useConfigBasedTimeFilter();
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showCsvUploadModal, setShowCsvUploadModal] = useState(false);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<string | null>(null);
  const [uploadErrors, setUploadErrors] = useState<string[]>([]);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshingTagId, setRefreshingTagId] = useState<number | null>(null);
  const [selectedTags, setSelectedTags] = useState<Set<number>>(new Set());
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  const { 
    data: tags, 
    loading, 
    createTag, 
    updateTag, 
    deleteTag, 
    uploadCsv,
    refreshCounts,
    refreshSingleCount,
    bulkDeleteTags 
  } = useTags({ 
    type: activeTab as 'manual' | 'system', 
    sortColumn: sortColumn || undefined, 
    sortDirection,
    showAll: activeTab === 'manual' ? showAllManualTags : showAllSystemTags 
  });

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };



  const handleTabSelect = (tab: string | null) => {
    if (tab) {
      setActiveTab(tab);
      setSelectedTags(new Set()); // Clear selections when switching tabs
    }
  };

  const handleShowAllSystemTags = () => {
    setShowAllSystemTags(!showAllSystemTags);
  };

  const handleShowAllManualTags = () => {
    setShowAllManualTags(!showAllManualTags);
  };

  const handleCreateTag = async () => {
    if (!newTagName.trim()) {
      alert('Tag name cannot be empty');
      return;
    }
    setIsCreating(true);
    try {
      await createTag(newTagName);
      setNewTagName('');
      setShowCreateForm(false);
    } catch (error) {
      console.error('Error creating tag:', error);
      alert('Failed to create tag');
    } finally {
      setIsCreating(false);
    }
  };

  const handleEditTag = async () => {
    if (currentTag) {
      if (!currentTag.name.trim()) {
        alert('Tag name cannot be empty');
        return;
      }
      setIsEditing(true);
      try {
        await updateTag(currentTag.id, currentTag.name);
        setShowEditModal(false);
        setCurrentTag(null);
      } catch (error) {
        console.error('Error editing tag:', error);
        alert('Failed to update tag');
      } finally {
        setIsEditing(false);
      }
    }
  };

  const handleDeleteTag = async () => {
    if (tagToDeleteId) {
      setIsDeleting(true);
      try {
        await deleteTag(tagToDeleteId);
        setShowDeleteConfirmModal(false);
        setTagToDeleteId(null);
      } catch (error) {
        console.error('Error deleting tag:', error);
        alert('Failed to delete tag');
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const handleCsvFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setCsvFile(file);
    setUploadResult(null);
  };

  const handleCsvUpload = async () => {
    if (!csvFile) {
      alert('Please select a CSV file to upload');
      return;
    }

    setIsUploading(true);
    setUploadResult(null);
    setUploadErrors([]);
    setUploadSuccess(null);

    try {
      // Validate CSV first
      const validation = await validateTagsCsvFile(csvFile);
      
      // Show errors and duplicates if any
      if (validation.errors.length > 0 || validation.duplicateCount > 0) {
        let message = `Validation results - Processing filtered data:\n`;
        
        if (validation.duplicateCount > 0) {
          message += `• ${validation.duplicateCount} duplicate emails filtered out\n`;
        }
        
        if (validation.errors.length > 0) {
          message += `• ${validation.errors.length} rows with errors excluded\n`;
          message += `Errors (showing first ${Math.min(validation.errors.length, 10)}):`;
          if (validation.errors.length > 10) {
            message += `\n... and ${validation.errors.length - 10} more errors`;
          }
        }
        
        setUploadErrors(validation.errors);
        setUploadResult(message);
      }

      // Upload valid rows if any exist
      if (validation.validRows.length === 0) {
        setUploadResult('No valid rows to upload');
        setIsUploading(false);
        return;
      }

      // Create a new CSV with header + valid rows
      const text = await csvFile.text();
      const headerLine = text.split('\n')[0];
      const validCsvContent = [headerLine, ...validation.validRows].join('\n');
      const validCsvFile = new File([validCsvContent], csvFile.name, { type: 'text/csv' });

      const data = await uploadCsv(validCsvFile);
      const uploadedTagsCount = (data as any).uploadedTags?.length || 0;
      const totalChunks = (data as any).totalChunks || 1;
      const successfulChunks = (data as any).successful || 0;
      const failedChunks = (data as any).failed || 0;
      
      let successMessage = `Tag assignment completed\n`;
      if (totalChunks > 1) {
        successMessage += `• Processed in ${totalChunks} chunks (${successfulChunks} successful, ${failedChunks} failed)\n`;
      }
      successMessage += `• ${(data as any).stats?.subscribersFound || 0} subscribers found and processed\n`;
      successMessage += `• ${(data as any).stats?.successfulInserts || 0} new tag relationships created\n`;
      successMessage += `• ${(data as any).stats?.duplicates || 0} tag relationships already existed\n`;
      successMessage += `• ${(data as any).stats?.uniqueTags || 0} unique tags processed`;
      
      if ((data as any).stats?.subscribersNotFound > 0) {
        successMessage += `\n• ${(data as any).stats.subscribersNotFound} email addresses not found in subscribers`;
      }

      if (uploadedTagsCount > 0) {
        successMessage += `\n\nRefreshing counts for ${uploadedTagsCount} uploaded tag(s)...`;
      }
      
      // Show success in a separate state if there were validation errors
      if (validation.errors.length > 0) {
        setUploadSuccess(successMessage);
      } else {
        setUploadResult(successMessage);
      }
      
      setCsvFile(null);
      const fileInput = document.getElementById('csvTagFileInput') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
    } catch (error: any) {
      console.error('Error uploading CSV:', error);
      setUploadResult(`Error: ${error instanceof Error ? error.message : 'Upload failed'}`);
    } finally {
      setIsUploading(false);
    }
  };

  const handleCloseCsvModal = () => {
    setShowCsvUploadModal(false);
    setCsvFile(null);
    setUploadResult(null);
    setUploadErrors([]);
    setUploadSuccess(null);
    // Reset file input
    const fileInput = document.getElementById('csvTagFileInput') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  };

  const handleRefreshCounts = async () => {
    setIsRefreshing(true);
    try {
      await refreshCounts();
      alert('Tag counts refreshed successfully');
    } catch (error) {
      console.error('Error refreshing tag counts:', error);
      alert('Failed to refresh tag counts');
    } finally {
      setIsRefreshing(false);
    }
  };

  const filteredTags = useMemo(() => {
    if (!searchTerm) return tags;
    return tags.filter(tag => 
      tag.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [tags, searchTerm]);

  const handleSelectTag = (tagId: number) => {
    const newSelected = new Set(selectedTags);
    if (newSelected.has(tagId)) {
      newSelected.delete(tagId);
    } else {
      newSelected.add(tagId);
    }
    setSelectedTags(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedTags.size === filteredTags.length) {
      setSelectedTags(new Set());
    } else {
      setSelectedTags(new Set(filteredTags.map(tag => tag.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedTags.size === 0) return;
    
    setIsBulkDeleting(true);
    try {
      await bulkDeleteTags(Array.from(selectedTags));
      setSelectedTags(new Set());
      setShowBulkDeleteModal(false);
    } catch (error) {
      console.error('Error bulk deleting tags:', error);
      alert('Failed to delete some tags');
    } finally {
      setIsBulkDeleting(false);
    }
  };

  return (
    <Card className="m-3">
      <Card.Header as="h5" className="d-flex justify-content-between align-items-center" style={{ backgroundColor: '#fce4ec' }}>
        Manage Tags
        <div>
          {selectedTags.size > 0 && (
            <Button 
              variant="danger" 
              onClick={() => setShowBulkDeleteModal(true)} 
              className="me-2"
            >
              Delete Selected ({selectedTags.size})
            </Button>
          )}
          <Button variant="info" onClick={handleRefreshCounts} disabled={isRefreshing} className="me-2">
            {isRefreshing ? <Spinner animation="border" size="sm" /> : "Refresh Counts"}
          </Button>
          <Button variant="success" onClick={() => setShowCsvUploadModal(true)} className="me-2">
            Upload Tags CSV
          </Button>
          <Button variant="primary" onClick={() => setShowCreateForm(!showCreateForm)}>
            {showCreateForm ? 'Cancel' : 'Create New Tag'}
          </Button>
        </div>
      </Card.Header>
      <Card.Body>
        {showCreateForm && (
          <Form className="mb-3">
            <Form.Group className="mb-2">
              <Form.Control
                type="text"
                placeholder="New Tag Name (alphanumeric, -, _ only)"
                value={newTagName}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^a-zA-Z0-9_-]/g, '');
                  setNewTagName(value);
                }}
              />
            </Form.Group>
            <Button onClick={handleCreateTag} disabled={isCreating}>
              {isCreating ? <Spinner animation="border" size="sm" /> : "Add Tag"}
            </Button>
          </Form>
        )}
        
        <Form className="mb-3">
          <InputGroup>
            <Form.Control
              type="text"
              placeholder="Search tags..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <Button 
                variant="outline-secondary" 
                onClick={() => setSearchTerm('')}
              >
                Clear
              </Button>
            )}
          </InputGroup>
        </Form>
        {loading ? (
          <div className="d-flex justify-content-center align-items-center" style={{ height: '100px' }}>
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading Tags...</span>
            </Spinner>
          </div>
        ) : tags.length === 0 ? (
          <p>No tags found. Create one above!</p>
        ) : (
          <Tabs activeKey={activeTab} onSelect={handleTabSelect} id="tags-tabs" className="mb-3">
            <Tab eventKey="manual" title="Manual Tags">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <span className="text-muted">
                  {getStatusText(showAllManualTags, manualTimeFilter)}
                </span>
                <Button 
                  variant="outline-secondary" 
                  size="sm"
                  onClick={handleShowAllManualTags}
                >
                  {getToggleButtonText(showAllManualTags, manualTimeFilter)}
                </Button>
              </div>
              <Table id="manual-tags-table" striped bordered hover responsive className="mb-0">
                <thead>
                  <tr>
                    <th style={{ width: '50px' }}>
                      <div className="">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={filteredTags.length > 0 && selectedTags.size === filteredTags.length}
                          onChange={handleSelectAll}
                        />
                      </div>
                    </th>
                    <th onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>
                      Tag Name{' '}
                      {sortColumn === 'name' && (
                        <span>{sortDirection === 'asc' ? '▲' : '▼'}</span>
                      )}
                    </th>
                    <th onClick={() => handleSort('createdAt')} style={{ cursor: 'pointer' }}>
                      Created At{' '}
                      {sortColumn === 'createdAt' && (
                        <span>{sortDirection === 'asc' ? '▲' : '▼'}</span>
                      )}
                    </th>
                    <th onClick={() => handleSort('subscriberCount')} style={{ cursor: 'pointer' }}>
                      Subscribers Assigned{' '}
                      {sortColumn === 'subscriberCount' && (
                        <span>{sortDirection === 'asc' ? '▲' : '▼'}</span>
                      )}
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTags.map((tag) => (
                    <tr key={tag.id}>
                      <td>
                        <div className="">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={selectedTags.has(tag.id)}
                            onChange={() => handleSelectTag(tag.id)}
                          />
                        </div>
                      </td>
                      <td>{tag.name}</td>
                      <td>{formatDate(tag.createdAt)}</td>
                      <td>{tag.subscriberCount}</td>
                      <td>
                        <Button
                          variant="info"
                          size="sm"
                          className="me-2"
                          disabled={refreshingTagId === tag.id}
                          onClick={async () => {
                            setRefreshingTagId(tag.id);
                            await refreshSingleCount(tag.name);
                            setRefreshingTagId(null);
                          }}
                        >
                          {refreshingTagId === tag.id ? 'Loading...' : 'Refresh'}
                        </Button>
                        <Button
                          variant="primary"
                          size="sm"
                          className="me-2"
                          onClick={() => {
                            setCurrentTag(tag);
                            setOriginalTagName(tag.name);
                            setShowEditModal(true);
                          }}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => {
                            setTagToDeleteId(tag.id);
                            setShowDeleteConfirmModal(true);
                          }}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Tab>
            <Tab eventKey="system" title="System Generated Tags">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <span className="text-muted">
                  {getStatusText(showAllSystemTags, systemTimeFilter)}
                </span>
                <Button 
                  variant="outline-secondary" 
                  size="sm"
                  onClick={handleShowAllSystemTags}
                >
                  {getToggleButtonText(showAllSystemTags, systemTimeFilter)}
                </Button>
              </div>
              <Table id="system-tags-table" striped bordered hover responsive className="mb-0">
                <thead>
                  <tr>
                    <th style={{ width: '50px' }}>
                      <div className="">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={filteredTags.length > 0 && selectedTags.size === filteredTags.length}
                          onChange={handleSelectAll}
                        />
                      </div>
                    </th>
                    <th onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>
                      Tag Name{' '}
                      {sortColumn === 'name' && (
                        <span>{sortDirection === 'asc' ? '▲' : '▼'}</span>
                      )}
                    </th>
                    <th onClick={() => handleSort('createdAt')} style={{ cursor: 'pointer' }}>
                      Created At{' '}
                      {sortColumn === 'createdAt' && (
                        <span>{sortDirection === 'asc' ? '▲' : '▼'}</span>
                      )}
                    </th>
                    <th onClick={() => handleSort('subscriberCount')} style={{ cursor: 'pointer' }}>
                      Subscribers Assigned{' '}
                      {sortColumn === 'subscriberCount' && (
                        <span>{sortDirection === 'asc' ? '▲' : '▼'}</span>
                      )}
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTags.map((tag) => (
                    <tr key={tag.id}>
                      <td>
                        <div className="">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={selectedTags.has(tag.id)}
                            onChange={() => handleSelectTag(tag.id)}
                          />
                        </div>
                      </td>
                      <td>{tag.name}</td>
                      <td>{formatDate(tag.createdAt)}</td>
                      <td>{tag.subscriberCount}</td>
                      <td>
                        <Button
                          variant="info"
                          size="sm"
                          className="me-2"
                          disabled={refreshingTagId === tag.id}
                          onClick={async () => {
                            setRefreshingTagId(tag.id);
                            await refreshSingleCount(tag.name);
                            setRefreshingTagId(null);
                          }}
                        >
                          {refreshingTagId === tag.id ? 'Loading...' : 'Refresh'}
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => {
                            setTagToDeleteId(tag.id);
                            setShowDeleteConfirmModal(true);
                          }}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Tab>
          </Tabs>
        )}
      </Card.Body>

      {/* Edit Tag Modal */}
      <Modal id="edit-tag-modal" show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Tag</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Tag Name</Form.Label>
            <Form.Control
              id="edit-tag-name-input"
              type="text"
              value={currentTag?.name || ''}
              onChange={(e) => {
                const value = e.target.value.replace(/[^a-zA-Z0-9_-]/g, '');
                setCurrentTag(currentTag ? { ...currentTag, name: value } : null);
              }}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Close
          </Button>
          <Button variant="primary" onClick={handleEditTag} disabled={isEditing || (currentTag?.name === originalTagName)}>
            {isEditing ? <Spinner animation="border" size="sm" /> : "Save Changes"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteConfirmModal} onHide={() => setShowDeleteConfirmModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to delete this tag?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteConfirmModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteTag} disabled={isDeleting}>
            {isDeleting ? <Spinner animation="border" size="sm" /> : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Bulk Delete Confirmation Modal */}
      <Modal show={showBulkDeleteModal} onHide={() => setShowBulkDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Bulk Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete {selectedTags.size} selected tag(s)? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowBulkDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleBulkDelete} disabled={isBulkDeleting}>
            {isBulkDeleting ? <Spinner animation="border" size="sm" /> : `Delete ${selectedTags.size} Tags`}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* CSV Upload Modal for Tag Assignment */}
      <Modal show={showCsvUploadModal} onHide={handleCloseCsvModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Upload Tags CSV</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <h6>CSV Format Instructions:</h6>
            <p>Upload a CSV file with exactly 2 columns:</p>
            <ul>
              <li><strong>email</strong> - The subscriber's email address</li>
              <li><strong>tags</strong> - Tags separated by dashes (e.g., "newsletter-premium-vip")</li>
            </ul>
            <p><strong>Example CSV content:</strong></p>
            <pre style={{ backgroundColor: '#f8f9fa', padding: '10px', borderRadius: '4px', fontSize: '12px' }}>
{`email,tags
user@example.com,newsletter-premium
test@example.com,newsletter-free-welcome
admin@example.com,admin-newsletter-vip`}
            </pre>
            <p><strong>Important:</strong> Only existing subscribers (by email) will have tags assigned. Non-existent emails will be skipped.</p>
          </div>
          
          <Form.Group className="mb-3">
            <Form.Label>Select CSV File</Form.Label>
            <Form.Control
              id="csvTagFileInput"
              type="file"
              accept=".csv"
              onChange={handleCsvFileChange}
            />
          </Form.Group>

          {csvFile && (
            <div className="mb-3">
              <p><strong>Selected file:</strong> {csvFile.name}</p>
            </div>
          )}

          {uploadResult && (
            <div className="mb-3">
              <div className={`alert ${uploadResult.startsWith('Success') || uploadResult.startsWith('Tag assignment') ? 'alert-success' : 'alert-danger'}`} style={{ whiteSpace: 'pre-line' }}>
                {uploadResult}
                {uploadErrors.length > 0 && (
                  <div className="mt-3">
                    <div>
                      {uploadErrors.slice(0, 10).map((error, index) => (
                        <div key={index}>- {error}</div>
                      ))}
                    </div>
                    {uploadErrors.length > 10 && (
                      <div className="mt-2">
                        <Button 
                          variant="outline-danger" 
                          size="sm"
                          onClick={() => {
                            const errorText = uploadErrors.join('\n');
                            const blob = new Blob([errorText], { type: 'text/plain' });
                            const url = window.URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = 'upload-errors.txt';
                            a.click();
                            window.URL.revokeObjectURL(url);
                          }}
                        >
                          View More ({uploadErrors.length - 10} more)
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {uploadSuccess && (
            <div className="mb-3">
              <div className="alert alert-success" style={{ whiteSpace: 'pre-line' }}>
                {uploadSuccess}
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseCsvModal}>
            Close
          </Button>
          <Button 
            variant="primary" 
            onClick={handleCsvUpload} 
            disabled={!csvFile || isUploading}
          >
            {isUploading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Uploading...
              </>
            ) : (
              'Upload Tags CSV'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
};

export default Tags;
